/**
 * A SHOPPER'S STEP, HANDED TO THE SERVER AS WELL AS TO THE PIXEL.
 *
 * The browser pixel loses events: iOS, blockers, a tab closed before its request left. For a shop whose
 * payload says so (`metaServerEvents`), each product view, add to cart and checkout begun is also posted
 * to POST /public/salons/:slug/shopper-steps, which keeps it for Stylon's own funnel and sends Meta the
 * server's copy — under the event id the pixel sent it with (utils/metaPixel.js), so Meta merges the two.
 *
 * How it leaves:
 *   · `fetch` with `keepalive`, so a checkout's step sent as the tab closes still goes;
 *   · the pixel's `_fbp` / `_fbc` as X-Meta-Fbp / X-Meta-Fbc (utils/metaPixelCookies.js): the API is
 *     another site, so the cookies themselves never reach it, and it reads them from those headers only;
 *   · naming the ad its visit came from — the campaign and the click, as utils/adVisit.js remembers the
 *     visit (this one, or a return within seven days), or the campaign the landing address names while
 *     its click is not remembered yet — only on the pages of the business that ad was for. The server
 *     credits the step to that campaign, on whichever page, reload or new tab it happens; a step that
 *     names none is credited to the visitor's last recorded ad click, and until 2026-09-17 every step
 *     was, so a visit whose own landing was not recorded gave its steps to an earlier click;
 *   · without credentials — a step is the shop's to report, not the signed-in customer's;
 *   · never from a browser driven by automation (`navigator.webdriver`): a test run or a bot is not a
 *     shopper;
 *   · never in a way that can throw into the page.
 */
import API from '../Config/Api';
import { metaPixelCookieHeaders } from './metaPixelCookies';
import { adOfLocation, currentAdVisit, isVisitOfAd } from './adVisit';
import { getVisitorKey } from './visitorKey';

function doorOf(slug) {
  return `/public/salons/${encodeURIComponent(slug)}/shopper-steps`;
}

/** What the door takes as a campaign and a click id: anything else in an address would cost the step itself. */
const CAMPAIGN_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CLICK_ID = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * `{ campaignId, adClickId }` of the ad the visit on shop `slug` came from — only what is known — or null.
 * A landing whose click is not remembered yet names its campaign from the address; a remembered visit
 * counts only on its own business's shop.
 */
function adOfVisit(slug) {
  const landing = typeof window !== 'undefined' && window.location ? adOfLocation(window.location.search) : null;
  const visit = currentAdVisit();
  if (landing && !isVisitOfAd(visit, landing)) return CAMPAIGN_ID.test(landing.campaignId || '') ? { campaignId: landing.campaignId } : null;
  if (!visit || !visit.slug || String(visit.slug).toLowerCase() !== String(slug).toLowerCase()) return null;
  const ad = {};
  if (CAMPAIGN_ID.test(visit.campaignId || '')) ad.campaignId = visit.campaignId;
  if (CLICK_ID.test(visit.clickId || '')) ad.adClickId = visit.clickId;
  return ad.campaignId || ad.adClickId ? ad : null;
}

/** Hand `step` ({ eventName, eventId, eventSourceUrl, contents, value, currency }) to the door of shop `slug`. */
export function relayShopperStep(slug, step) {
  try {
    if (!slug || !step || typeof window === 'undefined') return;
    if (typeof navigator !== 'undefined' && navigator.webdriver === true) return;
    const visitorKey = getVisitorKey();
    const body = visitorKey ? { ...step, visitorKey } : { ...step };
    Object.assign(body, adOfVisit(slug));
    const cookies = metaPixelCookieHeaders();
    if (typeof window.fetch === 'function') {
      const base = String((API && API.defaults && API.defaults.baseURL) || '/v1').replace(/\/+$/, '');
      Promise.resolve(
        window.fetch(`${base}${doorOf(slug)}`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', ...cookies },
          body: JSON.stringify(body),
        }),
      ).catch(() => {});
      return;
    }
    Promise.resolve(API.post(doorOf(slug), body, { headers: cookies })).catch(() => {});
  } catch (_) {
    /* never throw from instrumentation */
  }
}
